import { useTranslation } from 'next-i18next';
import { NewBadge } from '@/components/NewBadge';
import { cn } from '@/utils/tailwind.util';
import { BOARD_TEMPLATES } from '../constants';
import type { BoardTemplate } from '../types/template';
import { getBoardTypeLabel } from '../utils/boardTypeLabel';

// 기존 상대 순서는 유지하되, 그룹형 → 담벼락형 순으로만 정렬 (Array.sort는 안정 정렬).
const ORDERED_TEMPLATES = [...BOARD_TEMPLATES].sort((a, b) => Number(b.type === 'GROUP') - Number(a.type === 'GROUP'));
const boardTypePreviewMotionClassName = 'pointer-events-none absolute left-0 top-[-4px] h-[calc(100%+9px)] w-full object-cover transition-[top] duration-150 ease-out group-hover:top-[-8px] group-focus-visible:top-[-8px] motion-reduce:transition-none';
const templateDescriptionMotionClassName = 'max-h-0 translate-y-1 overflow-hidden break-keep text-caption-md-medium text-alternative opacity-0 transition-[max-height,opacity,transform] duration-150 ease-out group-hover:max-h-[32px] group-hover:translate-y-0 group-hover:opacity-100 group-focus-visible:max-h-[32px] group-focus-visible:translate-y-0 group-focus-visible:opacity-100 motion-reduce:transition-none';
interface TemplatePickerProps {
  onSelect: (template: BoardTemplate) => void;
  onCreateBlank: (type: BoardTemplate['type']) => void;
  disabled?: boolean;
}

// 라벨은 getBoardTypeLabel 이 소유한다(my-board 카드·학급 chip 과 공용).
// 여기 남는 건 이 화면 전용 프리뷰(배경색·썸네일)뿐.
const BOARD_TYPES: {
  type: BoardTemplate['type'];
  previewClassName: string;
  imageSrc: string;
}[] = [{
  type: 'WALL',
  previewClassName: 'bg-violet-300',
  imageSrc: '/assets/board-template-wall-type@2x.webp'
}, {
  type: 'GROUP',
  previewClassName: 'bg-blue-300',
  imageSrc: '/assets/board-template-group-type@2x.webp'
}, {
  type: 'TIMELINE',
  previewClassName: 'bg-yellow-600',
  imageSrc: '/assets/board-template-timeline-type@2x.webp'
}];
export function TemplatePicker({
  onSelect,
  onCreateBlank,
  disabled
}: TemplatePickerProps) {
  const {
    t
  } = useTranslation('board');
  const {
    t: tTemplate
  } = useTranslation('boardTemplates');
  return <div className="flex flex-col" data-sentry-component="TemplatePicker" data-sentry-source-file="TemplatePicker.tsx">
      <section className="pb-2xl">
        <div className="flex flex-col gap-xs pb-lg">
          <h2 className="text-heading-lg-extrabold text-default">
            {t('board.new.blank.title')}
          </h2>
          <p className="text-body-md-medium text-neutral">
            {t('board.new.blank.description')}
          </p>
        </div>

        <ul className="grid max-w-[620px] grid-cols-1 gap-md tablet-sm:grid-cols-3">
          {BOARD_TYPES.map(({
          type,
          previewClassName,
          imageSrc
        }) => <li key={type}>
              <button type="button" disabled={disabled} onClick={() => onCreateBlank(type)} className={cn('group flex w-full min-w-[160px] flex-col items-start gap-sm text-left disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50')}>
                <span className={cn('relative flex aspect-[206/132] w-full items-center justify-center overflow-hidden rounded-md border-4 border-opacity-white-6 shadow-sm transition-shadow duration-150 ease-out group-hover:shadow-md group-focus-visible:shadow-md motion-reduce:transition-none', previewClassName)}>
                  <img src={imageSrc} alt="" className={boardTypePreviewMotionClassName} />
                  <span className="absolute inset-[-4px] bg-opacity-white-3 opacity-0 transition-opacity duration-150 ease-out group-hover:opacity-100 group-focus-visible:opacity-100 motion-reduce:transition-none" />
                </span>
                {/* NEW 뱃지는 썸네일 위가 아니라 라벨 옆에 둔다 — 새로 생긴 건
                    그림이 아니라 "선택지"이고, 세 유형을 좌우로 비교하며 읽는
                    시선이 라벨 줄에서 끝나 여기서 자연스럽게 눈에 들어온다. */}
                <span className="flex w-full items-center gap-3xs px-xs text-body-md-semibold text-default transition-colors duration-150 ease-out group-hover:text-neutral group-focus-visible:text-neutral motion-reduce:transition-none">
                  {getBoardTypeLabel(t, type)}
                  {type === 'TIMELINE' &&
              // 장식이라 접근명에서 뺀다 — 버튼 이름은 "타임라인" 그대로.
              // 16px 은 라벨(text-body-md = 16px)과 같은 높이라 줄을 밀지 않는다.
              // NewBadge 기본은 18px 이지만 여기선 라벨 옆 보조 표식이라 한 단계 낮춘다.
              <span aria-hidden="true" className="flex shrink-0">
                      <NewBadge className="size-[16px] text-[10px]" />
                    </span>}
                </span>
              </button>
            </li>)}
        </ul>
      </section>

      <section>
        <div className="flex flex-col gap-xs pb-lg">
          <h2 className="text-heading-lg-extrabold text-default">
            {t('board.new.template.title')}
          </h2>
          <p className="text-body-md-medium text-neutral">
            {t('board.new.template.description')}
          </p>
        </div>

        <ul className="grid grid-cols-1 gap-md tablet-sm:grid-cols-3">
          {ORDERED_TEMPLATES.map(template => <li key={template.id}>
              <button type="button" disabled={disabled} onClick={() => onSelect(template)} className={cn('group relative flex h-[78px] w-full items-center gap-sm overflow-hidden rounded-md border border-assistive bg-default px-md py-sm text-left shadow-xs transition-shadow duration-150 ease-out enabled:hover:shadow-sm enabled:focus-visible:shadow-sm disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none')}>
                <span aria-hidden="true" className="absolute right-[15px] top-[11px] rounded-full border border-alternative bg-default px-2xs text-caption-md-semibold text-assistive">
                  {getBoardTypeLabel(t, template.type)}
                </span>
                <span className="relative size-[54px] shrink-0 overflow-hidden rounded-sm border border-opacity-assistive bg-strong-alternative">
                  {template.thumbnail ? <img src={template.thumbnail} alt="" className="h-full w-full object-cover" /> : null}
                  <span className="absolute inset-[-1px] bg-opacity-white-4 opacity-0 transition-opacity duration-150 ease-out group-hover:opacity-100 group-focus-visible:opacity-100 motion-reduce:transition-none" />
                </span>
                <span className="flex min-w-0 flex-1 flex-col justify-center gap-0 transition-[gap] duration-150 ease-out group-hover:gap-4xs group-focus-visible:gap-4xs motion-reduce:transition-none">
                  <span className="truncate text-body-md-semibold text-default">
                    {tTemplate(template.nameKey)}
                  </span>
                  <span className={templateDescriptionMotionClassName}>
                    {tTemplate(template.descriptionKey)}
                  </span>
                </span>
              </button>
            </li>)}
        </ul>
      </section>
    </div>;
}